import { SocketContext } from "@components/Context/SocketContext";
import { TabsTotals } from "@components/Pagination/Pagination.props";
import {
  FixMinersList,
  MTFiltersValuesI,
  MTSort,
  SortDirection,
  SortFieldsToMinersSort,
} from "@services/getMinerList";
import {
  MinerRecordsResponse,
  MinerTabType,
  RecordsResponse,
  fillNamedTotals,
} from "@services/http/responses/BaseResponse";
import useApiRequests from "@services/http/useApiRequest";
import { RootState } from "@store";
import { saveMTSearch } from "@store/filters/actions";
import { MTFilters } from "@store/filters/reducer";
import {
  addSelectedMiners,
  needDeselectAll,
  removeSelectedMiners,
  selectMiners,
  setRequestParams,
  setSubTabs,
} from "@store/minersSelect/actions";
import {
  SelectedMinerI,
  SelectionQuery,
  ServerSideFixMinersList,
  isMinerSelected,
  manyMinersSelected,
  noMinersSelected,
  tabIDToType,
} from "@store/minersSelect/actionTypes";
import {
  setMinersTablePage,
  setMinersTableSort,
  setMinersTableState,
} from "@store/minersTable/actions";
import { clearMiners, setMiners } from "@store/storedMiners/actions";
import deepEqual from "@utils/deepEqual";
import { dev } from "@utils/dev";
import formValuesToFilters from "@utils/formValuesToFilters";
import useArrayReorder from "@utils/hooks/useArrayReorder";
import { useAuth } from "@utils/hooks/useAuth";
import { useMTFilters } from "@utils/hooks/useFilters";
import { useFirstRender } from "@utils/hooks/useFirstRender";
import useGetMinerList, {
  IAdminMinerTableWithUnknown,
} from "@utils/hooks/useGetMinerList";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import {
  useStoredMinerTotal,
  useStoredMiners,
} from "@utils/hooks/useStoredMiners";
import { getSavedMiners, saveMinersLocal } from "@utils/localMiners";
import { sub, unsub } from "@utils/pubsub";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { TEMP_SAVE_MTABLE_STATE } from "../../../../config";
import { MinerRowsReorderData } from "../MinerRow/useMinerRowControl";
import { SearchPart } from "../Search/Search.props";
import { IMinersTableProps } from "./MinersTable.props";
import { MODALS_ENUM } from "@constants/Modals";
import { show } from "redux-modal";
import useMinerReproxyPortsCache from "./Miners.utils";
import { SocketSubTypes } from "@services/http/socket/requests";
import { IAdminMinerAdditionalData, IAdminMinerTable } from "@services/getMinerInfo";

export enum MinerSortFields {
  UNSET = -1,
  ID = 1,
  ALGO = 2,
  SPEED_D = 3,
  SPEED_C = 4,
  POOL = 5,
  STATE = 6,
  CUSTOMER = 7,
  TYPE = 8,
} // minus for reverse

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 20;

export interface ControlKeysStateI {
  ctrlKey: boolean;
  shiftKey: boolean;
}

interface BoundsI {
  min: number;
  max: number;
}

const emptySearch = (s?: string[]) =>
  s ? s.filter((x) => !!s.length).length === 0 : true;

const TAG = "MinersTableLog";

export const useMinersTableControl = (props: IMinersTableProps) => {
  const { formatMessage } = useIntl();
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const savedFilters = useRef<(fv: MTFilters) => MTFilters>();
  const firstRender = useFirstRender();
  //observer нигде не использовалось?
  //const observer = useRef<IntersectionObserver | undefined>();
  //
  const pulseAllCheckbox = useSelector(
    (state: RootState) => !!state.minersSelect.needDeselectAll
  );
  const tab = useSelector((state: RootState) =>
    tabIDToType(state.minersSelect.tab)
  );
  const tabRef = useRef(tab);
  const storedSort = useSelector((state: RootState) => state.minersTable.sort);
  const page = useSelector((state: RootState) => state.minersTable.Page);
  const pageSize = useSelector(
    (state: RootState) => state.minersTable.PageSize
  );
  const selectedMiners = useSelector((state: RootState) => state.minersSelect);
  const allMinersAreSelected = useSelector((state: RootState) =>
    manyMinersSelected(state.minersSelect)
  );
  const dragIDs = useSelector(
    (state: RootState) => state.minersSelect.dragMinersIDs
  );
  const total = useStoredMinerTotal();
  const filters = useMTFilters();
  const isAdmin = useAuth().isAdmin;
  const { onLoader } = useGlobalNotifs();
  const dispatch = useDispatch();
  const requests = useApiRequests();
  const auth = useSelector((state: RootState) => state.auth);
  const miners = useStoredMiners();
  const unknownMiners = useSelector(
    (state: RootState) => state.storedMiners.UnknownMiners
  );
  const [selectedAll2, setSelectedAll] = useState<boolean>(false); //? selectedAll2
  const [loading, setLoading] = useState<boolean>(false);
  //filters
  const [localFilters, setLocalFilters_] = useState<MTFilters>(filters);
  const [requestFilters, setRequestFilters] = useState<
    MTFiltersValuesI | undefined
  >();
  const [emptySearchPage, setEmptySearchPage] = useState<
    [number, number, boolean]
  >([page, pageSize, false]);
  const [_, setSelPos] = useState<number[]>();
  const [refreshLoad, setRefreshLoad] = useState<boolean>(false);
  const [unknownSearch, setUnknownSearch] = useState<string[]>([]);

  const [connected, setConnected] = useState(false);

  const [search, setSearch] = useState<SearchPart[]>([]);

  const subtab = useSelector((state: RootState) => state.minersSelect.subtab);
  const searchParts = useSelector(
    (state: RootState) => state.minersSelect.subTabs
  );
  const { getMinerList } = useGetMinerList();
  const {
    data: reorderedMiners,
    reset: resetReorder,
    reorder: reorderMiners,
  } = useArrayReorder(miners);

  // const selQuery: SelectionQuery = async (
  //   offset: number,
  //   limit: number,
  //   filter?: MTFiltersValuesI,
  //   sort?: MTSort,
  //   dir?: SortDirection
  // ) => {
  //   const res = await getMinerList(offset, limit, filter, sort, dir, tab);
  //   if (!res) return;
  //   const result = {
  //     miners: res.Records.map((el, i) =>
  //       makeSelectedMiner(
  //         el as IReceivedAdminMinerData,
  //         1 + Math.floor((offset + i) / pageSize)
  //       )
  //     ),
  //     total: res.Total,
  //   };
  //   return result;
  // };

  useEffect(resetReorder, [page, pageSize, tab, miners]);

  const makeSelectedMiner = (
    m: IAdminMinerTable,
    pg: number = 0
  ): SelectedMinerI => ({
    id: m.id.toString(),
    miner_id: m.miner_id,
    profile: m.profile,
    isOwnMining: m.is_own_mining,
    page: [pg || page || DEFAULT_PAGE, pageSize || DEFAULT_PAGE_SIZE],
  });

  //useEffect(() => console.log("UNKNOWN", unknownMiners, tab), [unknownMiners, tab]);

  const select = async (
    newPos: number,
    e?: ControlKeysStateI
  ): Promise<[SelectedMinerI[], SelectedMinerI[]]> => {
    let toAdd: number[] = [];
    let toRem: number[] = [];

    const calcBounds = (n: number[]): BoundsI => {
      const bounds = { min: miners.length, max: 0 };
      for (const p of n) {
        if (p < bounds.min) bounds.min = p;
        if (p > bounds.max) bounds.max = p;
      }
      if (bounds.min > bounds.max) {
        console.error("[calcBounds] Unknown error!");
      }
      return bounds;
    };

    const setBounds = (was: number[], pos: number): number[] => {
      if (e?.ctrlKey) {
        toAdd = [pos];
        toRem = was;
        return toAdd;
      }

      if (e?.shiftKey) {
        toAdd = [pos];
        const bounds = calcBounds(was);
        if (pos < bounds.min) {
          for (let r = pos + 1; r < bounds.min; r++) toAdd.push(r);
        } else if (pos > bounds.max) {
          for (let r = bounds.max + 1; r < pos; r++) toAdd.push(r);
        }
        return [...was, ...toAdd];
      }

      toAdd = [pos];
      return [...was, pos];
    };

    await new Promise<void>((reslv) =>
      setSelPos((was?: number[]) => {
        let result: number[] = [];
        if (was?.length) {
          const wpos = was.indexOf(newPos);
          if (wpos >= 0) {
            toRem = [newPos];
            reslv();
            result = was.splice(wpos, 1);
            return result;
          } else {
            reslv();
            result = setBounds(was, newPos);
            return result;
          }
        } else {
          toAdd = [newPos];
          reslv();
          result = toAdd;
          return result;
        }
      })
    );

    const mnrsToAdd: SelectedMinerI[] = [];
    const mnrsToRem: SelectedMinerI[] = [];

    for (const pos of toAdd) {
      const m = miners[pos];
      mnrsToAdd.push(makeSelectedMiner(m));
    }

    for (const pos of toRem) {
      const m = miners[pos];
      mnrsToRem.push(makeSelectedMiner(m));
    }

    return [mnrsToAdd, mnrsToRem];
  };

  // TODO:
  const fixSaved: ServerSideFixMinersList = async (ids) => {
    const resp = await requests.enqueue<RecordsResponse<SelectedMinerI>>(
      new FixMinersList(isAdmin, ids)
    );
    if (!resp) return;
    return resp.Records;
  };

  const setPage = (p: number, sz: number) => {
    // setLocalPage([p, sz]);
    if (TEMP_SAVE_MTABLE_STATE) {
      dispatch(setMinersTablePage(p, sz));
    }
  };

  /*useEffect(() => {
    const onDrag = (ev: MouseEvent) => {
      if (!dragIDs || !tableWrapperRef.current) return;
      const rect = tableWrapperRef.current.getBoundingClientRect();
      if (ev.clientY < 0 && rect.top < window.innerHeight / 2 - 100) {
        window.scrollBy(0, -50);
      } else if (
        ev.clientY > window.innerHeight &&
        rect.top - 100 > window.innerHeight
      ) {
        window.scrollBy(0, 50);
      }
    };

    window.addEventListener("mousemove", onDrag);
    return () => window.removeEventListener("mousemove", onDrag);
  }, [dragIDs]);*/

  useEffect(() => {
    //console.log("SEARCH 4", localFilters.values, typeof(localFilters.values));
    if (emptySearch(localFilters.values.search)) {
      const [ep, esz, reset] = emptySearchPage;
      if (reset) {
        setPage(ep, esz);
        setEmptySearchPage([ep, esz, false]);
      } else {
        const p = page;
        const sz = pageSize;
        setEmptySearchPage([p, sz, false]);
      }
    } else {
      const [ep, esz, reset] = emptySearchPage;
      if (!reset) setEmptySearchPage([ep, esz, true]);
    }
  }, [localFilters.values.search, page, pageSize]);

  /*useEffect(() => {
    (async () => {
      const ms = await getSavedMiners(fixSaved);
      if (!ms) return;
      const [mnrs, all] = ms;
      dispatch(selectMiners(all ? { ...mnrs, query: selQuery } : mnrs));
    })();
  }, []);*/

  useEffect(() => {
    if (props.page) dispatch(setMinersTablePage(props.page[0], props.page[1]));
  }, [props.page]);

  useEffect(() => {
    if (!allMinersAreSelected && savedFilters.current) {
      const fn = savedFilters.current;
      setLocalFilters(fn);
      dispatch(needDeselectAll(false));
      savedFilters.current = undefined;
    }
  }, [allMinersAreSelected]);

  useEffect(() => {
    if (firstRender) return;
    setLocalFilters_(filters);
  }, [filters]);

  const setLocalFilters = (fn: (x: MTFilters) => MTFilters) => {
    if (allMinersAreSelected) {
      savedFilters.current = fn;
      dispatch(needDeselectAll(true));
      return;
    }
    setLocalFilters_((ff) => {
      const f = fn(ff);
      const equal = (): boolean => {
        const fkeys = Object.keys(f);
        if (Object.keys(ff).length !== fkeys.length) {
          return false;
        }
        return fkeys.every(
          (k) => f[k as keyof MTFilters] === ff[k as keyof MTFilters]
        );
      };
      if (!equal()) {
        const [pg, pgsz] = [1, pageSize || DEFAULT_PAGE_SIZE];
        dispatch(setMinersTablePage(pg, pgsz));
      }
      return f;
    });
  };

  const prevLocalFiltersRef = useRef<MTFilters>(localFilters);

  useEffect(() => {
    if (localFilters) {
      if (deepEqual(prevLocalFiltersRef.current, localFilters)) return; // уменьшает на 1 рендер
      prevLocalFiltersRef.current = localFilters;
      // TODO нужно переработать поиск
      // добавил реплейс, потому что баг с поиском . иногда он с - , иногда нет ( потому чт в запрос должен идти без -)
      if (
        JSON.stringify(formValuesToFilters(localFilters)).replace("-", "") ===
        JSON.stringify(requestFilters)?.replace("-", "")
      )
        return;
      //dev.logt("requestFilters", "localFilters", localFilters);
      setRequestFilters(formValuesToFilters(localFilters));
    }
  }, [localFilters]);

  const minerPortsCache = useMinerReproxyPortsCache();

  function onGetMiners(
    values: IAdminMinerTableWithUnknown[],
    total: number,
    totals: TabsTotals
  ) {
    values.forEach(miner => {
      if ((miner as IAdminMinerAdditionalData).reproxy_link !== undefined) {
        const { reproxy_link } = miner as IAdminMinerAdditionalData;
        if (reproxy_link !== undefined) {
          minerPortsCache.set(
            miner.id,
            reproxy_link.Port,
            Date.now()
          );
        }
      }
    });
    dispatch(setMiners(values, tab === "unknown", total, totals));
  }

  const sortMinersBy = (sortField?: MinerSortFields) => {
    let srt: MTSort =
      SortFieldsToMinersSort[sortField || MinerSortFields.UNSET];
    dispatch(
      setMinersTableSort([
        srt,
        storedSort[0] === srt
          ? storedSort[1] === SortDirection.Asc
            ? SortDirection.Desc
            : SortDirection.Asc
          : SortDirection.Asc,
      ])
    );
  };

  const saveSort = () => {
    // dispatch(setMinersTableSort(sort));
  };

  useEffect(() => {
    const [srt, dir] = storedSort;
    const params = {
      filter: requestFilters,
      sort: srt,
      dir,
    };
    // console.log("SET REQUEST PARAMS", params);
    dispatch(setRequestParams(params));
  }, [requestFilters]);

  useEffect(() => {
    saveMinersLocal(selectedMiners);
  }, [selectedMiners]);

  useEffect(() => {
    const sTab: { [key in string]: boolean } = {};
    const sPos: number[] = [];

    setSelectedAll(manyMinersSelected(selectedMiners));

    if (noMinersSelected(selectedMiners)) {
      setSelPos([]);
      return;
    }

    for (const m of selectedMiners.selected) {
      sTab[m.id] = true;
    }

    for (const i in miners) {
      const m = miners[i];
      if (sTab.hasOwnProperty(m.id)) {
        sPos.push(+i);
      }
    }

    setSelPos(sPos);
  }, [miners, selectedMiners]);

  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);


  const getMinersList = useCallback(async (): Promise<MinerRecordsResponse<IAdminMinerTableWithUnknown> | void> => {
    try {
      setLoading(true);
      const options = {
        offset: (page - 1) * pageSize,
        filter: requestFilters,
        limit: pageSize,
        sort: storedSort[0],
        dir: storedSort[1],
        tab,
      };

      const res = await getMinerList(
        options.offset,
        options.limit,
        tab === "unknown" ? { search: unknownSearch } : options.filter,
        options.sort,
        options.dir,
        options.tab
      );

      if (!res || options.tab !== tabRef.current) return;
      //dbg.log("RES", res);
      onGetMiners(res.Records, res.Total, fillNamedTotals(res.Namedtotals));

      setConnected(!!(search.length || props.isFiltered));

      return res;
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    props.isFiltered,
    requestFilters,
    search.length,
    storedSort,
    tab,
    unknownSearch,
  ]);

  const prevPageRef = useRef<number>(DEFAULT_PAGE);
  const prevPageSizeRef = useRef<number>(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    prevPageRef.current = page;
    prevPageSizeRef.current = pageSize;
  }, [page, pageSize]);

  useEffect(() => {
    if (firstRender) return;
    dispatch(clearMiners());
    setLoading(true);
  }, [tab]);

  useEffect(() => {
    if (firstRender) return;
    if (!requestFilters) return;
    // dev.logt("requestFilters", "requestFilters", requestFilters);
    if (prevPageRef.current !== DEFAULT_PAGE) {
      setPage(DEFAULT_PAGE, prevPageSizeRef.current);
    } else {
      const t = setTimeout(async () => {
        getMinersList();
      }, 200); // помогает лишний раз не вызывать при ререндерах
      return () => clearTimeout(t);
    }
    // dispatch(setMinersTablePage(DEFAULT_PAGE, DEFAULT_PAGE_SIZE));
  }, [requestFilters]);

  useEffect(() => {
    if (firstRender) return; // т.к. на текущий момент всё равно вначале происходит изменение requestFilters
    const t = setTimeout(async () => {
      getMinersList();
    }, 200);
    return () => clearTimeout(t);
  }, [
    storedSort,
    page,
    pageSize,
    tab // === "unknown" && unknownSearch,
  ]);

  // useEffect(() => {
  //   dev.logt(
  //     "requestFilters", "tab changed", tab
  //   );
  //   if (firstRender) return;
  //   setPage(DEFAULT_PAGE, DEFAULT_PAGE_SIZE);
  //   // getMinersList();
  // }, [tab]);

  const selectedAll = useMemo(() => {
    const selected = new Set(selectedMiners.selected.map((m) => m.id));
    const numSelected = miners.filter((m) =>
      selected.has(m.id.toString())
    ).length;
    return !!numSelected && miners.length === numSelected;
  }, [selectedMiners.selected, miners]);

  useEffect(() => {
    setRefreshLoad(true);
    const t = setTimeout(() => setRefreshLoad(false), 500);
    return () => clearTimeout(t);
  }, [miners]);

  useEffect(() => {
    const s = searchParts.length
      ? searchParts.flatMap((part) => part.search)
      : [];

    setLocalFilters((f) => ({ ...f, values: { ...f.values, search: s } }));
    if (TEMP_SAVE_MTABLE_STATE) {
      dispatch(saveMTSearch(s));
    }
  }, [searchParts]);

  const subscribed = useRef<number[]>([]);
  const {
    subscribe,
    // unsubscribe,
    addReconnectListener,
    removeReconnectListener,
  } = useContext(SocketContext);



  useEffect(() => {
    addReconnectListener(onReconnected);
    return () => {
      removeReconnectListener(onReconnected);
    };
  }, []);

  const onReconnected = () => {
    if (subscribed.current.length > 0) {
      subscribe(SocketSubTypes.Miner, subscribed.current);
    }
  };

  useEffect(() => {
    const ids = miners.map((item) => item.id);
    if (ids.length > 0) {
      subscribed.current = ids;
      subscribe(SocketSubTypes.Miner, ids);
    }
    // return () => {
    //   if (subscribed.current.length > 0) {
    //     unsubscribe(SocketSubTypes.Miner, subscribed.current);
    //   }
    // };
  }, [miners]);

  useEffect(() => {
    const cb = (data: MinerRowsReorderData) => {
      //const [drag, ..._] = data.dragged;
      const was = miners
        .map((m, i) => [m, i] as [IAdminMinerTable, number])
        .filter(([m, i]) => data.dragged.includes("" + m.id))
        .map(([m, i]) => i);
      const become = reorderedMiners.findIndex((m) => m.id === data.to);
      //dbg.log("ONDROP", data, was, become);
      if (was.length && become >= 0) reorderMiners(was, become);
    };
    const unsubID = sub("miner-rows-reorder", cb);
    return () => unsub("miner-rows-reorder", unsubID);
  }, [reorderedMiners, miners]);

  const showPortsList = () => {
    dispatch(
      show(MODALS_ENUM.MINERS_PORTS)
    );
  }

  const [isMetaLoading, setMetaLoading] = useState(false);

  function createMinerMetaRow(line: number, miner: IAdminMinerTable): string {
    return `${line},${miner.miner_id},${miner.meta?.ip ?? ""},${miner.meta?.mac ?? ""},${miner.meta?.sn ?? ""}\n`;
  }

  const copyMinersMeta = useCallback(async () => {

    try {
      setMetaLoading(true);
      let line = 1;
      let result = "№,ID,IP,MAC,SN,PASSWORD\n";

      let page = 1;
      const pageLength = 100;

      let options = {
        offset: (page - 1) * pageLength,
        filter: requestFilters,
        limit: pageLength,
        sort: storedSort[0],
        dir: storedSort[1],
        tab: "all" as MinerTabType
      };

      let res = await getMinerList(
        options.offset,
        options.limit,
        tab === "unknown" ? { search: unknownSearch } : options.filter,
        options.sort,
        options.dir,
        options.tab
      );

      //console.log("copyMinersMeta", res);

      if (res) {
        res.Records.map(m => {
          result += createMinerMetaRow(line, m as IAdminMinerTable);
          line++;
        });

        let pagesCount = Math.ceil(res.Total / pageLength);
        page++;
        while (page <= pagesCount) {
          options = {
            offset: (page - 1) * pageLength,
            filter: requestFilters,
            limit: pageLength,
            sort: storedSort[0],
            dir: storedSort[1],
            tab: "all" as MinerTabType
          };
          res = await getMinerList(
            options.offset,
            options.limit,
            tab === "unknown" ? { search: unknownSearch } : options.filter,
            options.sort,
            options.dir,
            options.tab
          );
          if (res) {
            res.Records.map(m => {
              result += createMinerMetaRow(line, m as IAdminMinerTable);
              line++;
            });
          }
          page++;
        }
      }
      const blob = new Blob([result], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${props.TAG}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setMetaLoading(false);
    }
    //console.log("copyMinersMeta", result);
  }, [requestFilters, props.TAG])

  return {
    isAdmin: auth?.isAdmin,
    loading,
    miners: reorderedMiners,
    selectedAll,
    page,
    pageSize,
    refreshLoad,
    total,
    pulseAllCheckbox: pulseAllCheckbox
      ? formatMessage({ id: "machines.hint.deselect_all" })
      : undefined,
    tableWrapperRef,
    sort: storedSort,
    tab,
    subtab,
    unknownMiners,
    search,
    connected,

    makeSelectedMiner,
    sortMinersBy,
    saveSort,
    showPortsList,
    //selectAll,
    setPage,
    saveState(save: any) {
      if (!save) dispatch(setMinersTableState(DEFAULT_PAGE, DEFAULT_PAGE_SIZE));
    },
    setSearch(s_: SearchPart[]) {
      setSearch(s_);
      dispatch(setSubTabs(s_));
    },
    setUnknownSearch,
    //

    async selectMinerByID(id: any, e?: any) {
      onLoader(true);
      if (manyMinersSelected(selectedMiners)) {
        const ms = miners.filter((m) => m.id === id).map(makeSelectedMiner);
        dispatch(
          isMinerSelected(selectedMiners, id.toString())
            ? removeSelectedMiners(ms)
            : addSelectedMiners(ms)
        );
        onLoader(false);
        return;
      }
      const [toAdd, toRem] = await select(
        miners.findIndex((m) => m.id === id),
        e
      );
      if (toAdd.length) dispatch(addSelectedMiners(toAdd));
      if (toRem.length) dispatch(removeSelectedMiners(toRem));
      onLoader(false);
    },

    async selectAll() {
      onLoader(true);
      if (selectedAll) {
        setSelPos([]);
        dispatch(removeSelectedMiners(selectedMiners.selected));
        onLoader(false);
        return;
      }
      const toAddTab: Record<string, SelectedMinerI> = {};
      const selected = new Set(selectedMiners.selected.map((m) => m.id));
      const bounds: number[] = [];
      const mnrs = miners;
      for (let i = 0; i < mnrs.length; i++) {
        bounds.push(i);
        const m = mnrs[i];
        if (selected.has(m.id.toString())) continue;
        toAddTab[m.id] = makeSelectedMiner(m);
      }
      setSelPos(bounds);
      const toAdd = Object.values(toAddTab);
      if (toAdd.length) dispatch(addSelectedMiners(toAdd));
      onLoader(false);
    },
    isMetaLoading,
    copyMinersMeta,
  };
};
